import { FieldShell } from '@atlas/ui-components'
import { Combobox, type ComboboxOption, Input } from '@atlas/ui-primitives'
import { type ReactNode, useId } from 'react'

/**
 * Compact labelled-field helpers shared by the classification and workflow-admin
 * forms. Each composes the design-system {@link FieldShell}, so the label, its
 * association with the control, and the cell's spacing come from one place.
 *
 * @remarks
 * These stay as helpers rather than expanding at every call site: they pair a
 * label with a *specific* control and adapt its change event, which the call
 * sites would otherwise repeat verbatim. The choice field is the design
 * system's own `SelectField`, not a local copy of it.
 */

export function TextField({
	label,
	value,
	onChange,
	placeholder,
	error,
}: {
	label: string
	value: string
	onChange: (v: string) => void
	placeholder?: string
	error?: string
}) {
	const id = useId()
	return (
		<FieldShell label={label} htmlFor={id} error={error}>
			<Input
				id={id}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				placeholder={placeholder}
				aria-invalid={error ? true : undefined}
				aria-describedby={error ? `${id}-error` : undefined}
			/>
		</FieldShell>
	)
}

/**
 * A labelled {@link Combobox} — the app's picker, with a label above it.
 *
 * `Combobox` takes an `aria-label` rather than rendering a visible one, and these forms
 * want the label seen as well as announced. Preferred over a native select: it searches,
 * it indents a hierarchy, and it is what every other form in ATLAS uses, so a native
 * dropdown here reads as a different application.
 *
 * Composes {@link FieldShell} like its siblings, so the label and the cell's spacing come
 * from one place rather than being restated here.
 */
export function LabelledCombobox({
	label,
	value,
	onChange,
	options,
	placeholder,
	isDisabled,
}: {
	label: string
	value: string
	onChange: (value: string | undefined) => void
	options: ComboboxOption[]
	placeholder?: string
	isDisabled?: boolean
}) {
	return (
		// No `htmlFor`: `Combobox` takes no `id`, and it labels its own input via
		// `aria-label`. FieldShell still owns the visible label and the cell's spacing.
		<FieldShell label={label}>
			<Combobox
				aria-label={label}
				value={value || null}
				onChange={onChange}
				options={options}
				placeholder={placeholder}
				isDisabled={isDisabled}
			/>
		</FieldShell>
	)
}

export function FieldWrap({ label, children }: { label: string; children: ReactNode }) {
	return <FieldShell label={label}>{children}</FieldShell>
}
